import { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { base44 } from "@/api/base44Client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Plus, Trash2, Library } from "lucide-react";
import ExercisePickerDialogV2 from "@/components/exercises/ExercisePickerDialogV2";

const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

const empty = {
  name: "", description: "", difficulty: "intermediate",
  goal: "general_fitness", days_per_week: 3, exercises: [],
};

export default function WorkoutTemplateForm({ open, onOpenChange, onSubmit, initialData }) {
  const [form, setForm] = useState(initialData || empty);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerTargetIdx, setPickerTargetIdx] = useState(null);

  const { data: allExercises = [] } = useQuery({
    queryKey: ["exercises"],
    queryFn: () => base44.entities.Exercises.list("name", 2500),
    enabled: open,
  });
  const exerciseByName = useMemo(() => {
    const m = new Map();
    for (const e of allExercises) {
      if (e?.name) m.set(e.name.trim().toLowerCase(), e);
    }
    return m;
  }, [allExercises]);

  const openPicker = (idx) => { setPickerTargetIdx(idx); setPickerOpen(true); };

  const handleLibrarySelect = (exercise) => {
    const entry = {
      day: "Monday",
      name: exercise.name,
      sets: 3,
      reps: "10",
      rest_seconds: 60,
      notes: "",
      exerciseId: exercise.exerciseId,
      gifUrl: exercise.gifUrl,
      targetMuscles: exercise.targetMuscles,
    };
    if (pickerTargetIdx === null) {
      setForm(f => ({ ...f, exercises: [...f.exercises, entry] }));
    } else {
      const updated = [...form.exercises];
      updated[pickerTargetIdx] = { ...updated[pickerTargetIdx], ...entry, day: updated[pickerTargetIdx].day };
      setForm(f => ({ ...f, exercises: updated }));
    }
    setPickerTargetIdx(null);
  };

  const addExercise = () =>
    setForm(f => ({ ...f, exercises: [...f.exercises, { day: "Monday", name: "", sets: 3, reps: "10", rest_seconds: 60, notes: "" }] }));

  const updateExercise = (idx, field, value) => {
    const updated = [...form.exercises];
    updated[idx] = { ...updated[idx], [field]: value };
    setForm(f => ({ ...f, exercises: updated }));
  };

  const removeExercise = (idx) =>
    setForm(f => ({ ...f, exercises: f.exercises.filter((_, i) => i !== idx) }));

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(form);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display">{initialData ? "Edit Template" : "New Workout Template"}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <Label>Template Name</Label>
              <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="e.g. 4-Day Hypertrophy" required />
            </div>
            <div>
              <Label>Difficulty</Label>
              <Select value={form.difficulty} onValueChange={v => setForm({ ...form, difficulty: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="beginner">Beginner</SelectItem>
                  <SelectItem value="intermediate">Intermediate</SelectItem>
                  <SelectItem value="advanced">Advanced</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Goal</Label>
              <Select value={form.goal} onValueChange={v => setForm({ ...form, goal: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="strength">Strength</SelectItem>
                  <SelectItem value="hypertrophy">Hypertrophy</SelectItem>
                  <SelectItem value="endurance">Endurance</SelectItem>
                  <SelectItem value="fat_loss">Fat Loss</SelectItem>
                  <SelectItem value="general_fitness">General Fitness</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label>Description</Label>
            <Textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} rows={2} />
          </div>

          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label className="text-base font-display">Exercises</Label>
              <div className="flex gap-2">
                <Button type="button" variant="outline" size="sm" onClick={() => { setPickerTargetIdx(null); setPickerOpen(true); }} className="gap-1">
                  <Library className="w-3 h-3" /> From Library
                </Button>
                <Button type="button" variant="outline" size="sm" onClick={addExercise} className="gap-1">
                  <Plus className="w-3 h-3" /> Add Manual
                </Button>
              </div>
            </div>
            {form.exercises.map((ex, i) => {
              const fullEx = exerciseByName.get((ex.name || "").trim().toLowerCase());
              const gif = ex.gifUrl || ex.gif_url || fullEx?.gifUrl || fullEx?.gif_url || fullEx?.video_url;
              const targets = (Array.isArray(ex.targetMuscles) && ex.targetMuscles.length > 0)
                ? ex.targetMuscles
                : (Array.isArray(fullEx?.targetMuscles) ? fullEx.targetMuscles : []);

              return (
                <div key={i} className="p-3 rounded-xl bg-muted/50 space-y-2">
                  {gif && (
                    <div className="flex items-center gap-2 pb-1">
                      <img src={gif} alt={ex.name} className="w-10 h-10 rounded-lg object-cover bg-muted" />
                      {targets.length > 0 && (
                        <span className="text-xs text-muted-foreground capitalize">{targets.join(", ")}</span>
                      )}
                    </div>
                  )}
                  <div className="flex gap-2">
                    <Select value={ex.day} onValueChange={v => updateExercise(i, "day", v)}>
                      <SelectTrigger className="w-32"><SelectValue /></SelectTrigger>
                      <SelectContent>{DAYS.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
                    </Select>
                    <Input className="flex-1" value={ex.name} onChange={e => updateExercise(i, "name", e.target.value)} placeholder="Exercise name" />
                    <Button type="button" variant="ghost" size="icon" title="Pick from library" onClick={() => openPicker(i)} className="shrink-0 text-primary">
                      <Library className="w-4 h-4" />
                    </Button>
                    <Button type="button" variant="ghost" size="icon" onClick={() => removeExercise(i)} className="text-destructive shrink-0">
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    <Input type="number" value={ex.sets} onChange={e => updateExercise(i, "sets", parseInt(e.target.value))} placeholder="Sets" />
                    <Input value={ex.reps} onChange={e => updateExercise(i, "reps", e.target.value)} placeholder="Reps" />
                    <Input type="number" value={ex.rest_seconds} onChange={e => updateExercise(i, "rest_seconds", parseInt(e.target.value))} placeholder="Rest (s)" />
                  </div>
                </div>
              );
            })}
          </div>

          <Button type="submit" className="w-full">{initialData ? "Save Template" : "Create Template"}</Button>
        </form>
      </DialogContent>
      <ExercisePickerDialogV2 open={pickerOpen} onOpenChange={setPickerOpen} onSelect={handleLibrarySelect} />
    </Dialog>
  );
}